import { useState, type FormEvent } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CheckCircle2, XCircle } from "lucide-react";
import { confirmPasswordReset } from "@/services/auth";
import { PublicLegalFooter } from "@/components/PublicLegalFooter";

type State = "form" | "submitting" | "success" | "error" | "missing-token";

export function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token");

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [state, setState] = useState<State>(token ? "form" : "missing-token");
  const [validationError, setValidationError] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setValidationError("");

    if (password.length < 8) {
      setValidationError("Das Passwort muss mindestens 8 Zeichen lang sein.");
      return;
    }
    if (password !== confirm) {
      setValidationError("Die Passwörter stimmen nicht überein.");
      return;
    }
    if (!token) {
      setState("missing-token");
      return;
    }

    setState("submitting");
    try {
      await confirmPasswordReset(token, password);
      setState("success");
      window.setTimeout(() => navigate("/login?reset=1", { replace: true }), 3000);
    } catch (err) {
      setState("error");
      setErrorMessage(
        err instanceof Error && err.message
          ? "Der Link ist abgelaufen oder wurde bereits verwendet."
          : "Es ist ein Fehler aufgetreten. Bitte versuche es erneut.",
      );
    }
  }

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <CardTitle>QuizNudge</CardTitle>
          <CardDescription>Neues Passwort festlegen</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {state === "missing-token" && (
            <div className="space-y-4 text-center">
              <XCircle className="mx-auto h-12 w-12 text-destructive" />
              <h2 className="text-xl font-semibold">Ungültiger Link</h2>
              <p className="text-muted-foreground">
                Der Link zum Zurücksetzen ist ungültig oder abgelaufen. Bitte
                fordere einen neuen Link an.
              </p>
              <Button asChild variant="outline" className="w-full">
                <Link to="/forgot-password">Neuen Link anfordern</Link>
              </Button>
            </div>
          )}

          {state === "success" && (
            <div className="space-y-4 text-center">
              <CheckCircle2 className="mx-auto h-12 w-12 text-green-600" />
              <h2 className="text-xl font-semibold">Passwort gespeichert</h2>
              <p className="text-muted-foreground">
                Dein Passwort wurde erfolgreich geändert. Du wirst gleich zur
                Anmeldung weitergeleitet.
              </p>
              <Button asChild className="w-full">
                <Link to="/login?reset=1">Jetzt anmelden</Link>
              </Button>
            </div>
          )}

          {(state === "form" || state === "submitting" || state === "error") && (
            <form onSubmit={handleSubmit} className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Wähle ein neues Passwort mit mindestens 8 Zeichen.
              </p>
              <div className="space-y-2">
                <Label htmlFor="password">Neues Passwort</Label>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  autoComplete="new-password"
                  minLength={8}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirm">Passwort bestätigen</Label>
                <Input
                  id="confirm"
                  type="password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  required
                  autoComplete="new-password"
                  minLength={8}
                />
              </div>
              {validationError && (
                <p className="text-sm text-destructive" role="alert">
                  {validationError}
                </p>
              )}
              {state === "error" && (
                <p className="text-sm text-destructive" role="alert">
                  {errorMessage}
                </p>
              )}
              <Button
                type="submit"
                className="w-full"
                disabled={state === "submitting"}
              >
                {state === "submitting"
                  ? "Wird gespeichert …"
                  : "Passwort speichern"}
              </Button>
              <p className="text-center text-sm">
                <Link to="/login" className="text-blue-600 hover:underline">
                  Zurück zur Anmeldung
                </Link>
              </p>
            </form>
          )}
        </CardContent>
      </Card>
      <PublicLegalFooter />
    </div>
  );
}
